import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, MessageCircleWarning, XCircle, Clock, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { APROVACAO_PUBLICA_STATUS } from '../lib/constants';
import { Badge } from '../components/ui/Badge';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

function isImageUrl(url) {
  return /\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/i.test(url || '');
}
function isVideoUrl(url) {
  return /\.(mp4|webm|mov)(\?|$)/i.test(url || '');
}
function isPdf(url) { return /\.pdf(\?|$)/i.test(url || ''); }

export function AprovacaoPublicaPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [aprovacao, setAprovacao] = useState(null);
  const [err, setErr] = useState('');
  const [decisao, setDecisao] = useState(null); // 'aprovada' | 'alteracao_solicitada'
  const [comentario, setComentario] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/public/aprovacoes/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Erro');
        setAprovacao(data.aprovacao);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  const submit = async () => {
    if (!decisao) return;
    if (decisao === 'alteracao_solicitada' && !comentario.trim()) {
      alert('Por favor, descreva as alterações desejadas.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/public/aprovacoes/${token}/decisao`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decisao, comentario: comentario || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Falha');
      setFeedback(decisao);
      setAprovacao((a) => ({ ...a, status: decisao, decisao_comentario: comentario, decidido_em: new Date().toISOString() }));
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="pub-bg"><div className="pub-card center" style={{ minHeight: 200 }}><span className="spinner" /></div></div>;

  if (err) return (
    <div className="pub-bg">
      <div className="pub-card">
        <XCircle size={48} color="var(--c-danger)" style={{ margin: '0 auto 12px', display: 'block' }} />
        <h1 className="pub-title center">Link inválido</h1>
        <p className="muted center">{err}</p>
      </div>
    </div>
  );

  const a = aprovacao;
  const status = APROVACAO_PUBLICA_STATUS[a.status];
  const isDecidido = a.status !== 'pendente';

  return (
    <div className="pub-bg">
      <div className="pub-card">
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
            Aprovação — {a.secretaria_nome}
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>{a.titulo}</h1>
          <div className="text-sm muted">Demanda: {a.demanda_titulo}</div>
        </div>

        <div className="center" style={{ marginBottom: 14 }}>
          <Badge color={status.color}>
            {a.status === 'aprovada' && <CheckCircle2 size={12} style={{ marginRight: 3 }} />}
            {a.status === 'alteracao_solicitada' && <MessageCircleWarning size={12} style={{ marginRight: 3 }} />}
            {a.status === 'pendente' && <Clock size={12} style={{ marginRight: 3 }} />}
            {status.label}
          </Badge>
        </div>

        {a.descricao && (
          <div style={{ background: 'var(--c-surface-2)', padding: 12, borderRadius: 10, marginBottom: 16, fontSize: 14, whiteSpace: 'pre-wrap' }}>
            {a.descricao}
          </div>
        )}

        {a.arquivo_url && (
          <div style={{ marginBottom: 16 }}>
            <div className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Material</div>
            {isImageUrl(a.arquivo_url) ? (
              <a href={a.arquivo_url} target="_blank" rel="noreferrer">
                <img src={a.arquivo_url} alt="Arte" style={{ width: '100%', borderRadius: 12, border: '1px solid var(--c-border)' }} />
              </a>
            ) : isVideoUrl(a.arquivo_url) ? (
              <video src={a.arquivo_url} controls style={{ width: '100%', borderRadius: 12 }} />
            ) : isPdf(a.arquivo_url) ? (
              <iframe src={a.arquivo_url} style={{ width: '100%', height: 480, border: '1px solid var(--c-border)', borderRadius: 12 }} />
            ) : (
              <a href={a.arquivo_url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-block">
                <ExternalLink size={16} /> Abrir material
              </a>
            )}
          </div>
        )}

        {isDecidido ? (
          <div className={`result-box result-${a.status}`}>
            {a.status === 'aprovada' && <><CheckCircle2 size={32} /> <h2>Material aprovado!</h2></>}
            {a.status === 'alteracao_solicitada' && <><MessageCircleWarning size={32} /> <h2>Alteração solicitada</h2></>}
            {a.status === 'cancelada' && <><XCircle size={32} /> <h2>Pedido cancelado</h2></>}
            {a.status === 'expirada' && <><Clock size={32} /> <h2>Link expirado</h2></>}
            {a.decisao_comentario && (
              <div style={{ marginTop: 12, fontSize: 14, fontStyle: 'italic', opacity: 0.9 }}>
                "{a.decisao_comentario}"
              </div>
            )}
            {a.decidido_em && (
              <div className="text-xs muted" style={{ marginTop: 10 }}>
                em {format(new Date(a.decidido_em), 'dd/MM/yyyy HH:mm')}
              </div>
            )}
            <p className="text-sm muted" style={{ marginTop: 16 }}>A equipe de Comunicação já foi notificada.</p>
          </div>
        ) : (
          <>
            <div className="pub-action-grid">
              <button
                className={`pub-btn pub-btn-aprovar ${decisao === 'aprovada' ? 'selected' : ''}`}
                onClick={() => setDecisao('aprovada')}
                type="button"
              >
                <CheckCircle2 size={28} />
                <span>Aprovar</span>
              </button>
              <button
                className={`pub-btn pub-btn-alterar ${decisao === 'alteracao_solicitada' ? 'selected' : ''}`}
                onClick={() => setDecisao('alteracao_solicitada')}
                type="button"
              >
                <MessageCircleWarning size={28} />
                <span>Pedir alteração</span>
              </button>
            </div>

            {decisao && (
              <div style={{ marginTop: 16 }}>
                <label className="field">
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6, display: 'block' }}>
                    {decisao === 'aprovada' ? 'Comentário (opcional)' : 'Descreva as alterações *'}
                  </span>
                  <textarea
                    className="textarea"
                    rows={4}
                    placeholder={decisao === 'aprovada' ? 'Algum comentário? (opcional)' : 'O que precisa ser ajustado?'}
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                  />
                </label>
                <button
                  className={`btn btn-primary btn-block ${decisao === 'alteracao_solicitada' ? '' : ''}`}
                  style={{ marginTop: 12, padding: '14px 20px', fontSize: 15 }}
                  onClick={submit}
                  disabled={saving || (decisao === 'alteracao_solicitada' && !comentario.trim())}
                >
                  {saving ? 'Enviando...' : decisao === 'aprovada' ? 'Confirmar aprovação' : 'Enviar solicitação de alteração'}
                </button>
              </div>
            )}
          </>
        )}

        <div className="text-xs muted center" style={{ marginTop: 24, borderTop: '1px solid var(--c-border)', paddingTop: 16 }}>
          Secretaria de Comunicação · Prefeitura de Itapecerica-MG<br />
          Esta decisão será registrada no sistema.
        </div>
      </div>
    </div>
  );
}
